import React, { useState, useEffect } from 'react';
import { Calendar, MapPin } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { api } from '@/api';
import { formatDate, formatTime } from '@/utils/helpers';
import { PageHeroBanner } from '@/components/common/PageHeroBanner';

export const FixturesPage = ({ sports }) => {
  const [fixtures, setFixtures] = useState([]);
  const [selectedSport, setSelectedSport] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadFixtures = async () => {
      try {
        const params = selectedSport ? { sport_id: selectedSport } : {};
        const res = await api.getFixtures(params);
        setFixtures(res.data);
      } catch (error) {
        console.error('Error loading fixtures:', error);
      } finally {
        setLoading(false);
      }
    };
    loadFixtures();
  }, [selectedSport]);

  return (
    <div data-testid="fixtures-page">
      <PageHeroBanner 
        pageSlug="fixtures" 
        fallbackTitle="Fixtures"
        fallbackSubtitle="Upcoming games and schedules"
      />
      
      <div className="min-h-screen py-8 px-4">
        <div className="max-w-7xl mx-auto">
          {/* Sport Filter */}
          <div className="mb-8 flex flex-wrap gap-3">
            <Button
              variant={selectedSport === null ? "default" : "outline"}
              className={selectedSport === null ? "bg-sky-300" : "border-sky-400 text-sky-100"}
              onClick={() => setSelectedSport(null)}
            >
              All Sports
            </Button>
            {sports.map(sport => (
              <Button
                key={sport.id}
                variant={selectedSport === sport.id ? "default" : "outline"}
                className={selectedSport === sport.id ? "bg-sky-300" : "border-sky-400 text-sky-100"}
                onClick={() => setSelectedSport(sport.id)}
              >
                {sport.name}
              </Button>
            ))}
          </div>

          {/* Fixtures List */}
          {loading ? (
            <div className="space-y-4">
              {[...Array(5)].map((_, i) => (
                <div key={i} className="fixture-card animate-pulse p-6">
                  <div className="h-4 bg-gray-700 rounded w-1/4 mb-4" />
                  <div className="h-6 bg-gray-700 rounded w-1/2" />
                </div>
              ))}
            </div>
          ) : (
            <div className="space-y-4">
              {fixtures.map(fixture => (
                <div key={fixture.id} className="fixture-card p-6" data-testid={`fixture-${fixture.id}`}>
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-sm text-sky-200">{fixture.sport_name} / {fixture.league_name}</span>
                    {fixture.has_coverage && (
                      <Badge className="bg-green-500 text-white">Coverage Available</Badge>
                    )}
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="text-xl font-semibold text-white">
                        {fixture.home_team_name} vs {fixture.away_team_name}
                      </div>
                      {fixture.venue && (
                        <div className="text-sm text-gray-400 mt-1 flex items-center">
                          <MapPin className="w-4 h-4 mr-1" /> {fixture.venue}
                        </div>
                      )}
                    </div>
                    <div className="text-right">
                      <div className="text-lg text-white font-medium">{formatDate(fixture.scheduled_time)}</div>
                      <div className="text-sm text-gray-400">{formatTime(fixture.scheduled_time)}</div>
                    </div>
                  </div>
                </div>
              ))}

              {fixtures.length === 0 && (
                <div className="text-center py-12">
                  <Calendar className="w-16 h-16 text-gray-600 mx-auto mb-4" />
                  <p className="text-gray-400 text-lg">No upcoming fixtures</p>
                  <p className="text-gray-500">Check back later for scheduled games</p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default FixturesPage;
